import * as calcApi from '@/api/calc';
import * as guiApi from '@/api/gui';
import { jobStateEnum } from '@/api/job';
import { BottomTabsKey, useTabKeyStore } from '@/utils/bottomTabsKeyStore';
import { useJobStore } from '@/utils/jobService';
import { useSplitterStore } from '@/utils/splitterStore';
import { CaretDownOutlined, CaretRightOutlined, CaretUpOutlined, FullscreenExitOutlined, FullscreenOutlined, LoadingOutlined, PauseCircleOutlined, PlayCircleOutlined } from '@ant-design/icons';
import { App, FloatButton, InputNumber, Slider, Switch } from 'antd';
import dayjs from 'dayjs';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
interface JsmolDisplayProps {
    path: string;
}

const JsmolDisplay: React.FC<JsmolDisplayProps> = ({ path }) => {
    const { message } = App.useApp();

    const [buttonLoading, setButtonLoading] = useState(false)
    const [charge, setCharge] = useState<number>(0);
    const [multiplicity, setMultiplicity] = useState<number>(1);
    const addJob = useJobStore((state => state.addJob))
    const setBottomTabsActiveKey = useTabKeyStore(state => state.setBottomTabsActiveKey);
    const setOutputTabsActiveKey = useTabKeyStore(state => state.setOutputTabsActiveKey);
    const {
        setVerticalSizes,
    } = useSplitterStore();
    const { t } = useTranslation('common');

    const [getGUIOptions, setGUIOptions] = useState<guiApi.GUIOptions | null>(null);

    const getOptions = async () => {
        const res = await guiApi.getGUIOptions(path);
        if (!res?.ok || !res?.data) {
            if (res?.message) {
                message.error(res?.message)
            } else {
                message.error(t('jsmol.gui_options_failed'))
            }
            return
        }
        console.log("GUI options:", res.data)
        setGUIOptions(res.data)
    }
    useEffect(() => {
        getOptions()
    }, [path])

    const start = async (type: "TSopt" | "optimize" | "ir" | "uvvis" | "raman" | "freq" | "optAndFreq") => {
        setButtonLoading(true)
        try {
            const user = localStorage.getItem('userName');
            const tag = user + "_" + dayjs().unix().toString();
            const resJob = await calcApi.postCalculation({
                path: path,
                type: type,
                comment: tag,
                charge: charge,
                multiplicity: multiplicity
            })

            if (!resJob?.ok) {
                message.error(resJob?.message)
                setButtonLoading(false)
                return
            }


            if (resJob.data) {
                addJob({
                    job_name: resJob.data?.job_name,
                    path: resJob.data?.path,
                    state: jobStateEnum.PENDING,
                    created_at: resJob.data?.time || Math.floor((dayjs().unix()) / 1000),
                    tag
                })
                setVerticalSizes(["80%", "20%"])
                setBottomTabsActiveKey(BottomTabsKey.OUTPUT)
                setOutputTabsActiveKey(tag);
            }

            message.success(t('jsmol.job_submitted'))
        } catch (e) {
            console.error(e)
            message.error(t('jsmol.job_failed'))
        } finally {
            setButtonLoading(false)
        }
    }

    let src = import.meta.env.VITE_JSMOL_PAGE;
    if (!src.startsWith('http://') && !src.startsWith('https://')) {
        const host = window.location.origin;
        src = `${host}${src}`;
    }
    console.log("iframe url:", src)
    const iframeRef = useRef<HTMLIFrameElement>(null);

    const setMolecule = () => {
        const token = localStorage.getItem('token');
        let fullRoute = import.meta.env.VITE_API_URL + "/resource/raw" + path;
        // Add host to the path if it's not already an absolute URL
        if (!fullRoute.startsWith('http://') && !fullRoute.startsWith('https://')) {
            const host = window.location.origin;
            fullRoute = `${host}${fullRoute}`;
        }
        console.log("data url:", fullRoute)
        iframeRef.current?.contentWindow?.postMessage({ path: fullRoute, token }, src);
        if (path.endsWith('.cube')) {
            const scripts = [
                // Render MO phase surface and keep molecular geometry visible at the same time.
                `isosurface sign red blue ${fullRoute}`
            ]
            scripts.forEach(script => {
                iframeRef.current?.contentWindow?.postMessage({ script }, src);
            })
        }

    }

    useEffect(() => {
        if (iframeRef.current) {
            iframeRef.current.onload = () => {
                setMolecule();
            }
        }
    }, [iframeRef.current]);

    const [isFullScreen, setIsFullScreen] = useState(false);

    const onFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    const [startAnimation, setStartAnimation] = useState(false);

    const playAnimation = () => {
        if (vibrationPlaying) {
            playVibration();
        }
        const preState = startAnimation;
        setStartAnimation(!preState);
        const scripts = !preState ?
            [`animation MODE LOOP`, `animation ON`] :
            [`animation OFF`]
        scripts.forEach(script => {
            iframeRef.current?.contentWindow?.postMessage({ script }, src);
        })
    }

    const [vibrationPlaying, setVibrationPlaying] = useState(false);
    const [vibrationLevel, setVibrationLevel] = useState(1);

    const loadVibration = async () => {
        const res = await guiApi.getVibrationData(path, vibrationLevel);
        if (!res?.ok || !res?.data) {
            if (res?.message) {
                message.error(res?.message)
            } else {
                message.error(t('jsmol.vibration_failed'))
            }
            return
        }

        setVibrationPlaying(true);

        const scripts = [
            `load DATA "vibration"\n${res?.data?.vibrationData}\nEND "vibration"`,
            `vibration ON`,
            ...(res?.data?.arrowCommand.split(',') || [])]

        console.log("vibration scripts:", scripts)


        scripts.forEach(script => {
            iframeRef.current?.contentWindow?.postMessage({ script }, src);
        })
    }

    const playVibration = async () => {
        if (!getGUIOptions || getGUIOptions?.vibration?.levels === 0) {
            message.warning(t('jsmol.no_vibration'));
            return;
        }
        if (vibrationPlaying) return;

        if (startAnimation) {
            playAnimation();
        }
        loadVibration();

    }

    const stopVibration = () => {
        if (!vibrationPlaying) return;

        setVibrationPlaying(false);

        const scripts = [`vibration OFF`, `zap DATA "vibration"`, `DRAW arrow* DELETE`]

        scripts.forEach(script => {
            iframeRef.current?.contentWindow?.postMessage({ script }, src);
        })
    }

    const [featureOpen, setFeatureOpen] = useState(true);

    return (
        <div className='jsmol w-full h-full left-0 top-0 z-50' style={{ height: isFullScreen ? '100vh' : '100%', position: isFullScreen ? 'fixed' : 'relative' }}>

            {/* Charge and Multiplicity inputs */}
            <div style={{ userSelect: "none" }} className='absolute left-4 top-4 z-10 flex flex-row gap-2 p-1 rounded-lg'>
                <div className='flex flex-col items-end gap-1'>
                    <span className='text-sm text-right font-medium text-white dark:text-[#FFFFFF] w-full'>Charge</span>
                    <div className='flex flex-row'>
                        <div className=' my-auto jsmol-input'>
                            <InputNumber
                                size="small"
                                value={charge}
                                onChange={(value) => setCharge(value || 0)}
                                placeholder="0"
                                controls={false}
                                style={{ width: 36 }}
                                classNames={{ input: 'text-white dark:text-[#FFFFFF] bg-transparent' }}
                            />
                        </div>

                        <div className='flex flex-col ml-1'>
                            <CaretUpOutlined onClick={() => setCharge(prev => prev + 1)} style={{ color: "#ffffff" }} />
                            <CaretDownOutlined onClick={() => setCharge(prev => prev - 1)} style={{ color: "#ffffff" }} />
                        </div>
                    </div>
                </div>
                <div className='flex flex-col items-end gap-1'>
                    <span className='text-sm text-right font-medium text-white dark:text-[#FFFFFF]'>Mult</span>
                    <div className='flex flex-row'>
                        <div className=' my-auto jsmol-input'>
                            <InputNumber
                                size="small"
                                value={multiplicity}
                                onChange={(value) => setMultiplicity(value || 1)}
                                min={1}
                                controls={false}
                                placeholder="1"
                                style={{ width: 36 }}
                                classNames={{ input: 'text-white dark:text-[#FFFFFF] bg-transparent' }}
                            />
                        </div>
                        <div className='flex flex-col ml-1'>
                            <CaretUpOutlined onClick={() => { setMultiplicity(prev => prev + 1); }} style={{ color: "#ffffff" }} />
                            <CaretDownOutlined onClick={() => setMultiplicity(prev => prev > 1 ? prev - 1 : 1)} style={{ color: "#ffffff" }} />
                        </div>
                    </div>
                </div>
                {/* <div className=' h-full ml-2 w-48 rounded-sm'>
                    <div className='flex flex-row justify-between items-center mb-2'>
                        <span className='text-sm text-white dark:text-[#FFFFFF] text-right mr-2 font-medium'>Vibration</span>
                        <Switch checked={vibrationPlaying} onChange={(checked) => { checked ? playVibration() : stopVibration() }}></Switch>
                    </div>

                    <div className='flex flex-row items-center justify-between'>
                        <Slider
                            min={1}
                            disabled={getGUIOptions?.vibration.levels === 0}
                            style={{ width: 100 }}
                            max={getGUIOptions?.vibration.levels}
                            onChange={(value) => setVibrationLevel(value)}
                            onChangeComplete={
                                () => {
                                    if (vibrationPlaying) {
                                        loadVibration();
                                    }
                                }
                            }
                            value={vibrationLevel}
                        />
                        <span className='text-sm text-white dark:text-[#FFFFFF] text-right font-medium'>Level: {vibrationLevel}</span>
                    </div>

                </div> */}
            </div>

            <iframe ref={iframeRef} src={src} allowFullScreen={true} width="100%" height="100%" frameBorder="0" />
            <FloatButton.Group className=' absolute right-5 bottom-28'>
                <FloatButton tooltip="Play animation" onClick={playAnimation} icon={startAnimation ? <PauseCircleOutlined /> : <PlayCircleOutlined />} />
                <FloatButton tooltip="Full screen" icon={isFullScreen ? <FullscreenExitOutlined /> : <FullscreenOutlined />} onClick={() => onFullScreen()} />
                {/* <FloatButton onClick={loading ? undefined : start} icon={loading ? <LoadingOutlined /> : <CaretRightOutlined />} /> */}
            </FloatButton.Group>
            <FloatButton.Group
                className=' absolute right-5 bottom-12'
                trigger="click"
                onClick={() => setFeatureOpen(!featureOpen)}
                placement={'left'}
                open={featureOpen}
                icon={buttonLoading ? <LoadingOutlined /> : <CaretRightOutlined />}
            >
                <FloatButton onClick={buttonLoading ? undefined : () => start('optimize')} shape='square' description="opt" />
                <FloatButton onClick={buttonLoading ? undefined : () => start('TSopt')} shape='square' description="TSopt" />
                <FloatButton onClick={buttonLoading ? undefined : () => start('freq')} shape='square' description="freq" />
                <FloatButton onClick={buttonLoading ? undefined : () => start('optAndFreq')} shape='square' description={<span style={{ lineHeight: 0.8 }}>opt<br />+<br />freq</span>} />
                <FloatButton onClick={buttonLoading ? undefined : () => start('ir')} shape='square' description="IR" />
                <FloatButton onClick={buttonLoading ? undefined : () => start('uvvis')} shape='square' description="UV/vis" />
                <FloatButton onClick={buttonLoading ? undefined : () => start('raman')} shape='square' description="Raman" />
            </FloatButton.Group>
        </div>
    );
};

export default JsmolDisplay;